import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { api } from '@/lib/api/client';

// ============================================
// DATABASE SCHEMA
// ============================================

interface ScoutScanQueueItem {
  id: string;
  qrCode: string;
  rating?: number;
  comment?: string;
  createdAt: Date;
  retryCount: number;
}

interface ScoutQueueDB extends DBSchema {
  scanQueue: {
    key: string;
    value: ScoutScanQueueItem;
    indexes: {
      'by-created': Date;
    };
  };
}

// ============================================
// DATABASE INITIALIZATION
// ============================================

const DB_NAME = 'tendedloop-scout';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<ScoutQueueDB>> | null = null;

function getDB(): Promise<IDBPDatabase<ScoutQueueDB>> {
  if (!dbPromise) {
    dbPromise = openDB<ScoutQueueDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const store = db.createObjectStore('scanQueue', { keyPath: 'id' });
        store.createIndex('by-created', 'createdAt');
      },
    });
  }
  return dbPromise;
}

// ============================================
// QUEUE OPERATIONS
// ============================================

export async function queueScan(data: {
  qrCode: string;
  rating?: number;
  comment?: string;
}): Promise<string> {
  const db = await getDB();
  const item: ScoutScanQueueItem = {
    id: `scan-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    qrCode: data.qrCode,
    rating: data.rating,
    comment: data.comment,
    createdAt: new Date(),
    retryCount: 0,
  };
  await db.add('scanQueue', item);
  return item.id;
}

export async function getQueuedScans(): Promise<ScoutScanQueueItem[]> {
  const db = await getDB();
  return db.getAllFromIndex('scanQueue', 'by-created');
}

export async function getQueueCount(): Promise<number> {
  const db = await getDB();
  return db.count('scanQueue');
}

export async function removeFromQueue(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('scanQueue', id);
}

export async function incrementRetryCount(id: string): Promise<void> {
  const db = await getDB();
  const item = await db.get('scanQueue', id);
  if (item) {
    item.retryCount += 1;
    await db.put('scanQueue', item);
  }
}

// ============================================
// SYNC SERVICE
// ============================================

const MAX_RETRIES = 3;

export interface SyncResult {
  success: number;
  failed: number;
  totalXp: number;
}

export async function syncQueuedScans(): Promise<SyncResult> {
  const items = await getQueuedScans();
  const result: SyncResult = { success: 0, failed: 0, totalXp: 0 };

  for (const item of items) {
    if (item.retryCount >= MAX_RETRIES) {
      result.failed += 1;
      continue;
    }

    try {
      const scanResult = await api.post<{ xpAwarded: number }>('/scout/scan', {
        qrCode: item.qrCode,
        rating: item.rating,
        comment: item.comment,
      });
      await removeFromQueue(item.id);
      result.success += 1;
      result.totalXp += scanResult.xpAwarded;
    } catch {
      await incrementRetryCount(item.id);
      result.failed += 1;
    }
  }

  return result;
}

// ============================================
// ONLINE SYNC REGISTRATION
// ============================================

export function registerOnlineSync(
  onSync?: (result: SyncResult) => void
): () => void {
  const handleOnline = async () => {
    const count = await getQueueCount();
    if (count > 0) {
      const result = await syncQueuedScans();
      onSync?.(result);
    }
  };

  window.addEventListener('online', handleOnline);
  return () => window.removeEventListener('online', handleOnline);
}
